import { Link, useForm } from "@inertiajs/react";
import { Building2 } from "lucide-react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bizminer_app_accounts_path, bizminer_app_root_path } from "@/routes";
import type { BizminerOrganization } from "@/types/serializers";
import {
  type Competitor,
  CompetitorsField,
} from "./_components/CompetitorsField";

interface Props {
  organization: BizminerOrganization;
}

export default function New({ organization }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    account: {
      name: "",
      website: "",
      business_line: "",
      country: "",
      competitors: [] as Competitor[],
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(bizminer_app_accounts_path());
  };

  const updateCompetitors = (newCompetitors: Competitor[]) => {
    setData("account.competitors", newCompetitors);
  };

  return (
    <BizminerLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Create New Account
          </h1>
          <p className="text-muted-foreground">
            Add a new account to {organization.name}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Account Details</CardTitle>
                <CardDescription>
                  Enter the name for your new account
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="account-name">
                  Account Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="account-name"
                  type="text"
                  value={data.account.name}
                  onChange={(e) => setData("account.name", e.target.value)}
                  placeholder="Enter account name"
                  required
                  autoFocus
                />
                {errors["account.name"] && (
                  <p className="text-sm text-destructive">
                    {errors["account.name"]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-website">
                  Website <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="account-website"
                  type="url"
                  value={data.account.website}
                  onChange={(e) => setData("account.website", e.target.value)}
                  placeholder="https://example.com"
                  required
                />
                {errors["account.website"] && (
                  <p className="text-sm text-destructive">
                    {errors["account.website"]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-business-line">
                  Business Line / Industry{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="account-business-line"
                  type="text"
                  value={data.account.business_line}
                  onChange={(e) =>
                    setData("account.business_line", e.target.value)
                  }
                  placeholder="e.g., Sportswear, Technology, Healthcare"
                  required
                />
                {errors["account.business_line"] && (
                  <p className="text-sm text-destructive">
                    {errors["account.business_line"]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-country">
                  Country <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="account-country"
                  type="text"
                  value={data.account.country}
                  onChange={(e) => setData("account.country", e.target.value)}
                  placeholder="e.g., United States, Germany, Japan"
                  required
                />
                {errors["account.country"] && (
                  <p className="text-sm text-destructive">
                    {errors["account.country"]}
                  </p>
                )}
              </div>

              {/* Competitors */}
              <CompetitorsField
                competitors={data.account.competitors}
                onUpdate={updateCompetitors}
                errors={errors["account.competitors"]}
              />

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={
                    processing ||
                    !data.account.name ||
                    !data.account.website ||
                    !data.account.business_line ||
                    !data.account.country
                  }
                >
                  {processing ? "Creating..." : "Create Account"}
                </Button>
                <Link href={bizminer_app_root_path()}>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </BizminerLayout>
  );
}
